import { useEffect, useState } from 'react';

import { fetchVehicleDetails } from '@/api/vehiclesApi';
import type { VehicleDetails } from '@/api/vehiclesApi';
import { showErrorToast } from '@/errors/showErrorToast';
import { toAppError } from '@/errors/toAppError';

interface VehicleDetailsState {
    details: VehicleDetails | null;
    isLoading: boolean;
    isError: boolean;
}

interface UseVehicleDetailsResult extends VehicleDetailsState {
    retry: () => void;
}

/**
 * Fetches one vehicle's details for the van panel. Unlike POI history there is no cache —
 * position and state are live data that would only mislead if replayed stale. A 403 means
 * the per-user capability was revoked mid-session: `onForbidden` fires (no toast) and the
 * caller's revoke cascade closes the panel.
 */
export function useVehicleDetails(
    vehicleId: string | null,
    enabled: boolean,
    onForbidden?: () => void,
): UseVehicleDetailsResult {
    const [state, setState] = useState<VehicleDetailsState>({ details: null, isLoading: false, isError: false });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!enabled || vehicleId === null) {
            // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
            setState({ details: null, isLoading: false, isError: false });
            return;
        }

        const controller = new AbortController();
        // eslint-disable-next-line react-x/set-state-in-effect
        setState({ details: null, isLoading: true, isError: false });

        fetchVehicleDetails(vehicleId)
            .then((details) => {
                if (controller.signal.aborted) return;
                setState({ details, isLoading: false, isError: false });
            })
            .catch((error: unknown) => {
                // The selection moved on (or unmounted) — discard this stale result silently.
                if (controller.signal.aborted) return;
                if (toAppError(error).code === 'forbidden') {
                    onForbidden?.();
                    return;
                }
                showErrorToast(error);
                setState({ details: null, isLoading: false, isError: true });
            });

        return () => {
            controller.abort();
        };
        // onForbidden is ref-stable enough not to warrant re-fetching on identity change;
        // deliberately excluded so a new inline callback can't re-trigger the request.
        // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
    }, [vehicleId, enabled, attempt]);

    const retry = () => {
        setAttempt((a) => a + 1);
    };

    return { ...state, retry };
}
